import React from 'react';
import { toast } from 'react-toastify';
import { useContextSelector } from 'use-context-selector';
import { ScheduleCardProps } from '..';
import Button from '../../../components/Button';
import { CartContext } from '../../../contexts/CartContext';
import { ModuleContext } from '../../../contexts/ModuleContext';
import { PatientProps } from '../../../contexts/PatientContext';
import { RouterContext } from '../../../contexts/RouterContext';
import { ScheduleContext } from '../../../contexts/ScheduleContext';
import { typeLocationSchedule } from '../../../models/Schedule';
import { createScheduleRequest, ScheduleRequest } from '../../../models/ScheduleRequest';
import { ScheduleSpecialistRequest, ScheduleSpecialistRequestBuilder } from '../../../models/ScheduleSpecialistRequest';
import {
  saveSalesForceSchedule,
  saveSchedule,
  saveSpecialistSchedule,
  saveTeleorientationSchedule
} from '../../../modules/slots';
import { PAGES } from '../../../Router/pages';
import { gtmEvent } from '../../../utils/gtm';
import { PatientScheduleProps } from '../../Configurator';
import { isTomorrow } from 'date-fns';
import { InternalStatusCode } from '../../../utils/enums/validate-rules.enum';
import { isAxiosError } from '../../../utils/errors';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { FirebaseEvents } from '../../../utils/enums/firebase-events';

type ActionButtonProps = {
  patient?: PatientProps;
  setLoading: (value: boolean) => void;
  completedSchedules: ScheduleCardProps[];
  incompletedSchedules: PatientScheduleProps[];
  handleSubmitErrorCallBack: (codeError?: InternalStatusCode) => void;
  disable?: boolean;
};

const ActionButton: React.FC<ActionButtonProps> = ({
  patient,
  setLoading,
  completedSchedules,
  incompletedSchedules,
  handleSubmitErrorCallBack,
  disable = false
}) => {
  const { scheduleItems, units, setTokenSuccess, eavSlotsData, scheduleSpecialistSelected } = useContextSelector(
    ScheduleContext,
    state => state
  );
  const { goTo } = useContextSelector(RouterContext, state => state);
  const { findProduct } = useContextSelector(CartContext, state => state);
  const { selectedItemToSchedule: itemToSchedule } = useContextSelector(ModuleContext, state => state);

  const { logEvent } = useAnalytics();

  function mapSchedulesToApi(patient: PatientProps) {
    const schedules = createScheduleRequest(patient, scheduleItems, units);
    return schedules;
  }

  async function handleSubmitSchedule(schedule: ScheduleRequest) {
    try {
      setLoading(true);
      const response = await saveSchedule(schedule);
      if (response.token) setTokenSuccess && setTokenSuccess(response.token);
      goTo(PAGES.CONCLUSION.id);
      setLoading(false);
    } catch (error) {
      handleRequestScheduleErrors(error);
      setLoading(false);
    }
  }

  async function handleSubmitScheduleTeleorientation(schedule: ScheduleRequest) {
    try {
      setLoading(true);
      const response = await saveTeleorientationSchedule(schedule);
      if (response.token) setTokenSuccess && setTokenSuccess(response.token);
      goTo(PAGES.CONCLUSION.id);
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error('Não foi possível realizar o agendamento.');
      setLoading(false);
    }
  }

  const handleRequestScheduleErrors = (error: any) => {
    console.error(error);
    setLoading(false);

    if (isAxiosError(error)) {
      const codeError = error.response?.data?.code;
      if (codeError === InternalStatusCode.MEDICAL_RECORD_EXCEPTION)
        return handleSubmitErrorCallBack(InternalStatusCode.MEDICAL_RECORD_EXCEPTION);

      const statusCode = error.response?.data.statusCode;
      if (statusCode === InternalStatusCode.CREATE_APPOINTMENT_HOUR_LIMIT)
        return toast.error(
          'Desculpe-nos, os agendamentos para o dia seguinte estão encerrados. Por gentileza, selecione um novo dia e horário.'
        );
    }
    return handleSubmitErrorCallBack(InternalStatusCode.GENERIC_EXCEPTION);
  };

  async function handleSubmitScheduleSpecialist(schedule: ScheduleSpecialistRequest) {
    try {
      setLoading(true);
      const response = await saveSpecialistSchedule(schedule);

      if (response.token) setTokenSuccess && setTokenSuccess(response.token);

      logEvent({
        eventName: FirebaseEvents.schedule_complete,
        eventParams: {
          product_id: schedule.resource.specialistId,
          product_name: schedule.resource.name,
          date: `${schedule.date}T${schedule.hour}`,
          service_id: schedule.catalog.serviceId,
          service_name: schedule.catalog.name,
          attendanceType: schedule.attendanceType,
          unit_id: schedule.unit.catalogId,
          unit_name: schedule.unit.name,
          payment_type: schedule.payment?.type ?? '',
          type: schedule.appointmentType
        }
      });

      goTo(PAGES.CONCLUSION.id);

      setLoading(false);
    } catch (error) {
      handleRequestScheduleErrors(error);
      setLoading(false);
    }
  }

  async function handleEAVSubmit() {
    if (eavSlotsData) {
      setLoading(true);
      const { availableSchedules, patient } = eavSlotsData;
      const { schedule } = completedSchedules[0];
      const selectedSlotOption = schedule?.selectedSlotOptions?.[0];
      const product = findProduct(schedule.productId);

      if (!product) {
        toast.error('Produto não encontrada!');
        setLoading(false);
        return;
      }

      const selectedEAVDate = availableSchedules[selectedSlotOption?.date ?? ''].find(dates => {
        const dateFormat = new Date(dates.intervalStart);
        if (`${dateFormat.getHours()}:${dateFormat.getMinutes()}` === selectedSlotOption?.hour) {
          return dates;
        }
      });

      if (!selectedEAVDate) {
        toast.error('Data não encontrada!');
        setLoading(false);
        return;
      }

      const result = await saveSalesForceSchedule({
        patient,
        selectedSlotOptions: {
          start: selectedEAVDate.intervalStart,
          end: selectedEAVDate.intervalFinish
        }
      });

      if (result.error) {
        toast.error('Houve um erro ao procurar agenda!');
      }
      if (!result.error) {
        if (result.data.code) setTokenSuccess && setTokenSuccess(result.data.code);
        goTo(PAGES.CONCLUSION.id);
      }
    }
    setLoading(false);
  }

  const prepareSchedules = () => {
    gtmEvent({
      event: 'agendamento-einstein',
      categoria: scheduleItems[0]?.productName?.includes('gripe') ? 'Agendar-Vacina' : 'Agendar',
      acao: 'step-6',
      rotulo: 'confirmar-agendamento'
    });
    if (itemToSchedule === 'SPECIALIST') {
      const dateCheck = new Date();
      dateCheck.setHours(18);
      dateCheck.setMinutes(59);
      dateCheck.setSeconds(0);

      const dateNow = new Date();

      if (scheduleSpecialistSelected?.date && scheduleSpecialistSelected?.hour) {
        const scheduleDate = new Date(`${scheduleSpecialistSelected?.date}T${scheduleSpecialistSelected?.hour}`);

        if (dateNow >= dateCheck && isTomorrow(scheduleDate)) {
          toast.error(
            'Desculpe-nos, os agendamentos para o dia seguinte estão encerrados. Por gentileza, selecione um novo dia e horário.'
          );
          return;
        }
      }

      if (patient) {
        const scheduleSpecialist = ScheduleSpecialistRequestBuilder(scheduleSpecialistSelected);
        scheduleSpecialist.setPatient(patient);
        if (scheduleSpecialistSelected.payment) {
          const { type, healthInsurance, healthPlan, price } = scheduleSpecialistSelected.payment;
          scheduleSpecialist.setPayment({
            type: String(type),
            healthInsurance,
            healthPlan,
            price
          });
        }
        handleSubmitScheduleSpecialist(scheduleSpecialist.build());
      }
    }
    if (itemToSchedule === 'PRODUCT' || itemToSchedule === 'PRODUCT_ENV' || itemToSchedule === 'SERVICE_ENV') {
      if (completedSchedules?.[0].schedule.careType === typeLocationSchedule.EINSTEIN_TO_YOU) {
        handleEAVSubmit();
      } else {
        if (patient) {
          const firstPatientScheduleRequest = mapSchedulesToApi(patient);
          const [firstSchedule] = scheduleItems;
          if (firstSchedule.channel === 'TELEORIENTATION')
            handleSubmitScheduleTeleorientation(firstPatientScheduleRequest);
          else handleSubmitSchedule(firstPatientScheduleRequest);
        }
      }
    }
  };
  return <Button title="Agendar" disabled={incompletedSchedules.length !== 0 || disable} onClick={prepareSchedules} />;
};

export default ActionButton;
